import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { ConfigProvider, theme as antTheme } from 'antd';
import '../shared/locale';
import { useAntdLocale } from '../shared/i18n';
import { lightTheme, darkTheme, cssVariables } from './themeConfig';

const ThemeContext = createContext(null);

export const ThemeProvider = ({ children }) => {
  // 从 localStorage 读取主题设置，默认跟随系统
  const [themeMode, setThemeMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    if (saved) return saved;
    // 跟随系统偏好
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  // 监听系统主题变化（当设置为 auto 时）
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      const saved = localStorage.getItem('theme');
      if (!saved) {
        setThemeMode(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // 应用 CSS 变量到 document
  useEffect(() => {
    const variables = cssVariables[themeMode];
    Object.entries(variables).forEach(([key, value]) => {
      document.documentElement.style.setProperty(key, value);
    });

    // 设置 body 背景色
    document.body.style.backgroundColor = themeMode === 'dark' ? '#060b1d' : '#f5f7fb';
    document.body.style.colorScheme = themeMode;

    // 添加 theme class 到 html
    document.documentElement.setAttribute('data-theme', themeMode);
  }, [themeMode]);

  // 切换主题
  const toggleTheme = () => {
    const newTheme = themeMode === 'light' ? 'dark' : 'light';
    setThemeMode(newTheme);
    localStorage.setItem('theme', newTheme);
  };

  // 设置特定主题
  const setTheme = (theme) => {
    setThemeMode(theme);
    localStorage.setItem('theme', theme);
  };

  // 计算当前主题配置
  const currentTheme = useMemo(() => {
    const baseTheme = themeMode === 'dark' ? darkTheme : lightTheme;
    return {
      ...baseTheme,
      algorithm: themeMode === 'dark' ? antTheme.darkAlgorithm : antTheme.defaultAlgorithm,
    };
  }, [themeMode]);

  const antdLocale = useAntdLocale();

  const contextValue = useMemo(() => ({
    themeMode,
    isDark: themeMode === 'dark',
    toggleTheme,
    setTheme,
  }), [themeMode]);

  return (
    <ThemeContext.Provider value={contextValue}>
      <ConfigProvider
        locale={antdLocale}
        theme={currentTheme}
      >
        {children}
      </ConfigProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    // Fallback for hot reload or edge cases
    return {
      themeMode: 'light',
      isDark: false,
      toggleTheme: () => {},
      setTheme: () => {},
    };
  }
  return context;
};

export default ThemeContext;
